import { useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';

import { CerberusApi } from '@hcs/cerberus';
import { useLatestMutationData } from '@hcs/hooks';
import {
  PlacesSearchDocument,
  PlacesSearchQuery,
  PlacesSearchQueryVariables,
} from '@hcs/types';

export const usePlacesSearch = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const result = useLatestMutationData(
    useMutation<
      PlacesSearchQuery['searchPlace'],
      unknown,
      PlacesSearchQueryVariables
    >({
      mutationFn: async (placeTermInput: PlacesSearchQueryVariables) => {
        // Cancel any in-flight request
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await CerberusApi.fetchQuery<
          PlacesSearchQuery,
          PlacesSearchQueryVariables
        >(PlacesSearchDocument, placeTermInput, controller.signal);
        return response.searchPlace;
      },
    }),
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  return { ...result, cancel };
};
